import { Component, EventEmitter, Input, OnChanges, OnDestroy, Optional, Output, QueryList, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { of as ObservableOf } from 'rxjs/observable/of';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import { catchError, debounceTime, finalize, map, switchMap, take, takeUntil, timeout } from 'rxjs/operators';
import {
  difference as _difference,
  every as _every,
  filter as _filter,
  find as _find,
  groupBy as _groupBy,
  includes as _includes,
  isEmpty as _isEmpty,
  isNil as _isNil,
  map as _map,
  some as _some,
  union as _union,
  uniq as _uniq,
  uniqBy as _uniqBy,
  flatMap as _flatMap,
} from 'lodash';
import { ContactListPageService } from '../contact-list-page.service';
import { IOrgMasterItem, OrgMasterKind } from '../contact-list-page.interface';

/** 連続したマスタ再取得依頼を1本にまとめる待ち時間（ms） */
const ORG_MASTER_REFRESH_DEBOUNCE_MS = 50;

/** 親が複数の2列UIをまとめて再取得するときのオプション */
export interface IOrgMasterSelectSearchRefreshOptions {
  destroy$: Observable<any>;
  timeoutMs: number;
  onComplete?: () => void;
  /** 子 refresh が空のとき、一覧 service 側でキャッシュの事前読み込みを行うコールバック */
  preloadCacheOnEmptyChildLoad?: () => Observable<void>;
  hasOrgMasterUiKeys?: () => boolean;
  useDelayedFallbackComplete?: boolean;
  registerTimer?: (timerId: any) => void;
}

/** 左列の1行（名称グループまたはコード単位の行） */
interface IOrgMasterParentRow {
  rowId: string;
  label: string;
}

/**
 * 失注理由・コンタクト区分の2列選択コンポーネント
 *
 * 選択組織のマスタを左列（名称）と右列（[組織名] 名称）に分けて表示し、組織ごとの複数選択を行う。
 * 親の初回 `refreshOrgMasterSelectSearchMasters` 完了前にドロップダウンを開くと items 未構築により、左列が空になることを防ぎます。
 */
@Component({
  selector: 'app-org-master-select-search',
  templateUrl: './org-master-select-search.component.html',
  styleUrls: ['./org-master-select-search.component.scss'],
})
export class OrgMasterSelectSearchComponent implements OnChanges, OnDestroy {

  /** マスタの種類 */
  @Input() masterKind: OrgMasterKind;

  /** 検索条件の組織コード */
  @Input() organizationCodes: string[] = [];

  /** 未選択時の表示文言 */
  @Input() placeholder = '';

  /** 選択キー（組織コード_コード） */
  @Input() selectedKeys: string[] = [];

  /** 組織候補の再登録などでマスタ再取得を促すトークン */
  @Input() refreshToken = 0;

  /** マスタ取得用（モーダル起動時は一覧と同一インスタンスを親から渡す）*/
  @Input() contactListPageService: ContactListPageService;

  /** 選択キー変更イベント */
  @Output() selectedKeysChange = new EventEmitter<string[]>();

  /** 選択組織の候補 */
  items: IOrgMasterItem[] = [];

  /** 左列に表示する行 */
  parentRows: IOrgMasterParentRow[] = [];

  /** 右列に表示する候補 */
  childList: IOrgMasterItem[] = [];

  /** 左列で選択中の行ID（名称または組織コード_コード） */
  activeRowId = '';

  /** 左列の絞り込み文字列 */
  parentFilterText = '';

  /** 右列の絞り込み文字列 */
  childFilterText = '';

  /** マスタ取得中 */
  mastersLoading = false;

  /** マスタ再取得の依頼 */
  private readonly refreshRequest$ = new Subject<void>();

  /** 破棄通知 */
  private readonly destroy$ = new Subject<void>();

  /**
   * ViewChildren 上の失注理由・コンタクト区分2列UIをまとめて再取得します。
   */
  static scheduleRefresh(
    orgMasterSelectSearchComponents: QueryList<OrgMasterSelectSearchComponent> | null | undefined,
    options: IOrgMasterSelectSearchRefreshOptions,
  ): void {
    // onComplete が複数経路から二重に呼ばれないようにする
    let doneInvoked = false;
    const completeOnce = () => {
      if (doneInvoked) {
        return;
      }
      doneInvoked = true;
      // 親（一覧・モーダル）へ「2列UIの再取得が終わった」通知
      if (options.onComplete) {
        options.onComplete();
      }
    };

    // 子 refresh が空だったとき用：必要なら service 側キャッシュの事前読み込みをしてから completeOnce
    const completeAfterCachePreloadIfNeeded = () => {
      const hasKeys = options.hasOrgMasterUiKeys ? options.hasOrgMasterUiKeys() : false;
      // 復元キーが無い、またはキャッシュ事前読み込みコールバック未指定なら追加取得せず完了
      if (!hasKeys || !options.preloadCacheOnEmptyChildLoad) {
        completeOnce();
        return;
      }
      // 一覧 service が org 別マスタを API 取得しキャッシュの事前読み込みをしてから完了
      options.preloadCacheOnEmptyChildLoad().pipe(takeUntil(options.destroy$)).subscribe(() => completeOnce());
    };

    // 画面破棄時に親が clearTimeout できるよう timerId を登録
    const scheduleTimer = (fn: () => void, delayMs: number) => {
      const timerId = setTimeout(fn, delayMs);
      if (options.registerTimer) {
        options.registerTimer(timerId);
      }
    };

    // runRefresh の二重起動防止（changes / setTimeout(0) / 遅延フォールバックが競合しうる）
    let refreshStarted = false;
    const runRefresh = () => {
      if (refreshStarted || _isNil(orgMasterSelectSearchComponents)) {
        return;
      }
      const components = orgMasterSelectSearchComponents.toArray();
      // ViewChildren にまだ子が無い（changes 待ち・次ティック再試行に委ねる）
      if (_isEmpty(components)) {
        return;
      }
      refreshStarted = true;
      // 失注理由・コンタクト区分の各2列UIでマスタ再取得を並列実行
      ObservableForkJoin(_map(components, component => component.refreshMastersAndWait().pipe(
        // 1子だけ失敗しても他の結果は使う
        catchError(() => ObservableOf([] as IOrgMasterItem[])),
      ))).pipe(
        timeout(options.timeoutMs),
        // タイムアウト含む forkJoin 全体失敗は null としてキャッシュ事前読み込み経路へ
        catchError(() => ObservableOf(null)),
        takeUntil(options.destroy$),
      ).subscribe(results => {
        if (_isNil(results)) {
          completeAfterCachePreloadIfNeeded();
          return;
        }
        const childLoads = results as Array<IOrgMasterItem[] | null | undefined>;
        const hasUiKeys = options.hasOrgMasterUiKeys ? options.hasOrgMasterUiKeys() : false;
        const allChildLoadsEmpty = _every(childLoads, row => _isEmpty(row));
        // 初期表示でキーだけ復元済み・子 API が空 → service キャッシュの事前読み込みで In 復元（一覧初回検索）
        if (hasUiKeys && allChildLoadsEmpty) {
          completeAfterCachePreloadIfNeeded();
          return;
        }
        // 通常：子のマスタ取得が問題なければそのまま完了
        completeOnce();
      });
    };

    // 子が既に DOM にあれば即 refresh、なければ QueryList.changes を1回待つ
    const scheduleChildRefreshWhenPossible = () => {
      if (_isNil(orgMasterSelectSearchComponents)) {
        return;
      }
      if (orgMasterSelectSearchComponents.length > 0) {
        runRefresh();
        return;
      }
      orgMasterSelectSearchComponents.changes.pipe(
        take(1),
        takeUntil(options.destroy$),
      ).subscribe(() => runRefresh());
    };

    scheduleChildRefreshWhenPossible();
    // changes より先にマイクロタスクで子が載るケースの保険
    scheduleTimer(() => {
      if (!doneInvoked && !refreshStarted) {
        runRefresh();
      }
    }, 0);
    // 一覧のみ：timeoutMs 経過後も未完了ならキャッシュの事前読み込みまたは再 refresh で必ず onComplete
    if (options.useDelayedFallbackComplete) {
      scheduleTimer(() => {
        if (doneInvoked) {
          return;
        }
        // refresh は走ったが subscribe まで返ってこない（タイムアウト等）
        if (refreshStarted) {
          completeAfterCachePreloadIfNeeded();
          return;
        }
        // 遅延時点で子が載っていれば refresh をもう一度試す
        if (!_isNil(orgMasterSelectSearchComponents) && orgMasterSelectSearchComponents.length > 0) {
          runRefresh();
          return;
        }
        // 子が一度も現れない場合も検索続行のためキャッシュの事前読み込みまたは complete
        completeAfterCachePreloadIfNeeded();
      }, options.timeoutMs);
    }
  }

  /**
   * コンストラクタ（マスタ再取得の依頼を購読し、組織の連続変更時は最後の依頼の結果のみ反映する）
   * @param defaultContactListPageService コンタクト一覧ページサービス（親 providers から注入）
   */
  constructor(@Optional() private defaultContactListPageService: ContactListPageService) {
    // 組織の連続変更・refreshToken 連打時は最後の依頼だけ API に流し、読み込み中表示の後始末を finalize で担保
    this.refreshRequest$.pipe(
      debounceTime(ORG_MASTER_REFRESH_DEBOUNCE_MS),
      switchMap(() => this.loadMasterItems(false)),
      takeUntil(this.destroy$),
    ).subscribe(items => {
      if (!_isNil(items)) {
        this.items = items;
        this.rebuildParentList();
      }
    });
  }

  /**
   * 組織変更時にマスタを再取得します。
   * @param changes 変更内容
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.masterKind || changes.refreshToken) {
      this.refreshMastersWhenReady();
    }
    if (changes.organizationCodes) {
      const previous = changes.organizationCodes.previousValue as string[];
      if (changes.organizationCodes.firstChange
        || !this.areOrganizationCodesEqual(previous, this.organizationCodes)) {
        this.refreshMastersWhenReady();
      }
    }
    if (changes.selectedKeys && !changes.selectedKeys.firstChange) {
      this.ensureActiveRowId();
      this.rebuildChildList();
    }
  }

  /**
   * 購読を解除します。
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * ボタンに表示する選択中の候補ラベルを返します。
   * @returns 選択中の候補ラベル（読点区切り）
   */
  get selectedLabel(): string {
    return _map(_filter(this.items, item => _includes(this.selectedKeys, item.key)), item => item.label).join('、');
  }

  /**
   * マスタを再取得し、完了を通知します（親が初回の同期で利用）。
   * @returns 反映後の候補一覧
   */
  refreshMastersAndWait(): Observable<IOrgMasterItem[]> {
    return this.loadMasterItems(true).pipe(
      map(items => {
        if (!_isNil(items)) {
          this.items = items;
          this.rebuildParentList();
        }
        return this.items;
      }),
    );
  }

  /**
   * 選択組織のマスタを再取得します。
   */
  refreshMasters(): void {
    this.refreshRequest$.next();
  }

  /**
   * ドロップダウンを開いたときにマスタ候補を再取得します。
   * @param open 開いたとき true
   */
  onDropdownOpen(open: boolean): void {
    if (open) {
      this.refreshMastersWhenReady();
    }
  }

  /** masterKind が設定済みのときだけマスタ再取得を依頼します。 */
  refreshMastersWhenReady(): void {
    if (_isNil(this.masterKind)) {
      return;
    }
    this.refreshMasters();
  }

  /** 組織コード配列が同一か（順序含む） */
  private areOrganizationCodesEqual(a: string[] | undefined, b: string[] | undefined): boolean {
    const left = a || [];
    const right = b || [];
    if (left.length !== right.length) {
      return false;
    }
    return _every(left, (code, index) => code === right[index]);
  }

  /**
   * マスタを取得します。組織変更後の古いレスポンスは null を返して無視します。
   * @param clearBeforeLoad 取得前に表示候補をクリアする
   */
  private loadMasterItems(clearBeforeLoad: boolean): Observable<IOrgMasterItem[] | null> {
    if (_isNil(this.masterKind)) {
      return ObservableOf(null);
    }
    // マスタの種類と組織コードを取得します。
    const kind = this.masterKind;
    const orgCodes = [...(this.organizationCodes || [])];
    if (clearBeforeLoad) {
      this.items = [];
      this.parentRows = [];
      this.childList = [];
      this.activeRowId = '';
    }
    this.mastersLoading = true;
    // マスタを取得します。
    const pageService = this.contactListPageService || this.defaultContactListPageService;
    if (_isNil(pageService)) {
      return ObservableOf([]);
    }
    return pageService.searchOrgMasterItems(kind, orgCodes).pipe(
      map(items => {
        if (kind !== this.masterKind || !this.areOrganizationCodesEqual(orgCodes, this.organizationCodes || [])) {
          return null;
        }
        return this.filterItemsForTargetOrganizations(items, orgCodes);
      }),
      catchError(() => ObservableOf([])),
      finalize(() => {
        this.mastersLoading = false;
      }),
    );
  }

  /** 検索対象組織に属する候補だけに絞ります。 */
  private filterItemsForTargetOrganizations(items: IOrgMasterItem[], orgCodes: string[]): IOrgMasterItem[] {
    if (_isEmpty(orgCodes)) {
      return items;
    }
    return _filter(items, item => _includes(orgCodes, item.organizationCode));
  }

  /**
   * 左列（名称の一覧）を絞り込み文字列で絞り込み、かな順で作り直します。
   */
  rebuildParentList(): void {
    this.parentRows = this.buildParentRows()
      .filter(row => _includes(row.label, this.parentFilterText))
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
    this.ensureActiveRowId();
    this.rebuildChildList();
  }

  /**
   * 左列行を、同一組織内で名称が重複する場合はコード単位に分割して構築します。
   */
  private buildParentRows(): IOrgMasterParentRow[] {
    const rows: IOrgMasterParentRow[] = [];
    _uniq(_map(this.items, item => item.name)).forEach(name => {
      const group = _filter(this.items, item => item.name === name);
      const byOrg = _groupBy(group, 'organizationCode');
      const hasDupInOrg = _some(byOrg, list => list.length > 1);
      if (hasDupInOrg) {
        group.forEach(item => rows.push({ rowId: item.key, label: `${name} (${item.code})` }));
      } else {
        rows.push({ rowId: name, label: name });
      }
    });
    return rows;
  }

  /**
   * 左列の行IDに対応する候補を返します。
   * @param rowId 行ID（名称または組織コード_コード）
   */
  getItemsForParentRow(rowId: string): IOrgMasterItem[] {
    const orgCodes = this.organizationCodes || [];
    const itemByKey = _find(this.items, i => i.key === rowId);
    if (itemByKey) {
      return this.filterItemsForTargetOrganizations([itemByKey], orgCodes);
    }
    return this.filterItemsForTargetOrganizations(_filter(this.items, item => item.name === rowId), orgCodes);
  }

  /**
   * 左列の絞り込み後に表示中の行に属する候補をすべて返します（ヘッダ「全て選択」用）。
   */
  getItemsForVisibleParentRows(): IOrgMasterItem[] {
    return _uniqBy(
      _flatMap(this.parentRows, row => this.getItemsForParentRow(row.rowId)),
      item => item.key,
    );
  }

  /**
   * 右列表示用の左列行を、現在の左列一覧と選択キーから決めます。
   */
  private ensureActiveRowId(): void {
    if (this.activeRowId && _find(this.parentRows, row => row.rowId === this.activeRowId)) {
      return;
    }
    const selectedItem = _find(this.items, item => _includes(this.selectedKeys, item.key));
    if (selectedItem) {
      const row = _find(this.parentRows, r => r.rowId === selectedItem.key || r.rowId === selectedItem.name);
      if (row) {
        this.activeRowId = row.rowId;
        return;
      }
    }
    this.activeRowId = '';
  }

  /**
   * 右列（表示中の左列行の組織別候補）を絞り込み文字列で絞り込んで作り直します。
   */
  rebuildChildList(): void {
    this.childList = _filter(this.getItemsForParentRow(this.activeRowId),
      item => _includes(item.label, this.childFilterText));
  }

  /**
   * 右列に表示する左列行を切り替えます。
   * @param rowId 行ID
   */
  showChildList(rowId: string): void {
    this.activeRowId = rowId;
    this.rebuildChildList();
  }

  /**
   * 左列行のチェック変更（右列を切り替えてから選択状態を更新）
   * @param rowId 行ID
   * @param checked 選択するとき true
   */
  onParentCheckboxChange(rowId: string, checked: boolean): void {
    this.showChildList(rowId);
    this.setSelected(this.getItemsForParentRow(rowId), checked);
  }

  /**
   * 指定した候補がすべて選択済みかを返します。
   * @param targets 候補
   * @returns 1件以上あり、すべて選択済みのとき true
   */
  isAllSelected(targets: IOrgMasterItem[]): boolean {
    return targets.length > 0 && _every(targets, target => _includes(this.selectedKeys, target.key));
  }

  /**
   * 指定した候補の選択状態を変更し、選択キーを通知します。
   * @param targets 候補
   * @param checked 選択するとき true
   */
  setSelected(targets: IOrgMasterItem[], checked: boolean): void {
    if (!_isEmpty(targets) && checked && !this.activeRowId) {
      const row = _find(this.parentRows, r => r.rowId === targets[0].key || r.rowId === targets[0].name);
      this.showChildList(row ? row.rowId : targets[0].key);
    }
    const targetKeys = _map(targets, target => target.key);
    this.selectedKeysChange.emit(checked
      ? _union(this.selectedKeys, targetKeys)
      : _difference(this.selectedKeys, targetKeys));
  }
}



// WEBPACK FOOTER //
// ./src/app/page/contact-list-page/org-master-select-search/org-master-select-search.component.ts